// ==========================================================================
// Location Setup Page - After onboarding, verify user location
// Uses FSP branding for colors and logos
//
// The confirm + map sheets live in the shared LocationPicker component so
// the same flow can be reopened from the Weather tab.
//
// Flow based on user state:
// - If user HAS location: CHECK IT OUT → Confirm modal → CONFIRM or CHANGE
// - If user has NO location: CHECK IT OUT → Map modal → CONTINUE or CANCEL
// ==========================================================================

import { LocationPicker } from '@/components/location/LocationPicker';
import { useUser } from '@/hooks/useUser';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default function LocationSetupPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: user } = useUser();

  const fsp = user?.fsp;
  const primaryColor = fsp?.primary_color || '#ED974F';
  const secondaryColor = fsp?.secondary_color || '#c47535';

  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const userName = user?.name?.split(' ')[0] || 'there';
  const bankName = fsp?.name || 'Fortune Credit';

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Main Content */}
      <div className="flex-1 px-6 pt-12">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <h1 className="text-[28px] font-bold text-gray-900">
              {t('locationSetup.greeting', { name: userName })}
            </h1>
            <span className="text-[28px]">✋</span>
          </div>

          {fsp?.appIcon ? (
            <img
              src={fsp.appIcon}
              alt={fsp.name}
              className="w-[42px] h-[42px] rounded-full object-cover"
            />
          ) : (
            <div
              className="w-[42px] h-[42px] rounded-full flex items-center justify-center"
              style={{ backgroundColor: secondaryColor }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 22 C12 22 5 16 5 10 C5 4 12 1 12 1 C12 1 19 4 19 10 C19 16 12 22 12 22Z"
                  fill={primaryColor}
                />
              </svg>
            </div>
          )}
        </div>

        <p className="text-[15px] text-gray-600 mb-4">
          {t('locationSetup.welcomeText', { bankName })}
        </p>

        <p className="text-[15px] text-gray-500 mb-6">{t('locationSetup.description')}</p>

        <h2 className="text-[18px] font-bold text-gray-900 mb-4">
          {t('locationSetup.findForecast')}
        </h2>

        <button
          type="button"
          onClick={() => setIsPickerOpen(true)}
          className="h-[46px] px-6 rounded-xl text-white text-[13px] font-semibold tracking-wide"
          style={{ backgroundColor: primaryColor }}
        >
          {t('locationSetup.checkItOut')}
        </button>
      </div>

      <LocationPicker
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSaved={() => navigate('/home', { replace: true })}
      />
    </div>
  );
}
